import { GetIssueResponseDto } from '@issuemanager/api-client';
import { useUpdateSLAStatus } from '@issuemanager/client/features/issues';
import {
  IssueTimelineEventTypeEnum,
  SLAStateEnum,
} from '@issuemanager/constants';
import { FormControlLabel, Switch, styled } from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useIssueOutletContext } from '../../../hooks/useIssueOutletContext';
import { SLAConfirmModal } from './disable-sla-confirm-modal';
import { useCounter } from './hooks';

interface Props {
  issue: GetIssueResponseDto;
}

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-right: 0;
`;

export function SLASwitch({ issue }: Props) {
  const { t } = useTranslation('issue');
  const {
    daysLeft,
    hoursLeft,
    minutesLeft,
    killInterval,
    refreshTimeLeft,
    initiateInterval,
  } = useCounter({
    createdAt: issue.createdAt,
    slaDays: issue.slaDays,
  });

  const [isSLAModalOpen, setIsSLAModalOpen] = useState(false);

  const updateSLAStatus = useUpdateSLAStatus({
    code: issue.code,
  });

  const handleClose = (): void => {
    setIsSLAModalOpen(false);
  };

  const handleAccept = async (data: { message: string }): Promise<void> => {
    if (
      issue.slaEvent?.eventType === IssueTimelineEventTypeEnum.SLADeactivated
    ) {
      await updateSLAStatus.mutateAsync({
        state: SLAStateEnum.ACTIVATED,
        reason: data.message,
      });
    } else {
      await updateSLAStatus.mutateAsync({
        state: SLAStateEnum.DEACTIVATED,
        reason: data.message,
      });
    }
    killInterval();
    handleClose();
  };

  const handleChange = async (_event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSLAModalOpen(true);
    refreshTimeLeft();
    initiateInterval();
  };

  const { userRoles } = useIssueOutletContext();
  const isTypeManagerOfIssue = Boolean(userRoles.data?.isIssueTypeManager);
  const isManagerOfIssue = Boolean(userRoles.data?.isManager);
  const slaActivated =
    !issue.slaEvent ||
    issue.slaEvent.eventType === IssueTimelineEventTypeEnum.SLAActivated;
  const getSLATimeValues = () => ({
    days: issue.slaDays,
    daysLeft,
    hoursLeft: String(hoursLeft).padStart(2, '0'),
    minutesLeft: String(minutesLeft).padStart(2, '0'),
  });

  return (
    <>
      <StyledFormControlLabel
        control={
          <Switch
            checked={slaActivated}
            onChange={handleChange}
            disabled={!isManagerOfIssue && !isTypeManagerOfIssue}
            size='small'
            data-testid='sla-switch'
          />
        }
        label={
          !issue.slaEvent ||
          issue.slaEvent.eventType === IssueTimelineEventTypeEnum.SLAActivated
            ? t('view.sla.banner.activeSLA')
            : t('view.sla.banner.deactiveSLA')
        }
      />
      {issue.slaEvent?.eventType !==
        IssueTimelineEventTypeEnum.SLADeactivated &&
        (issue.slaDueDays && issue.slaDueDays < 0 ? (
          <Trans
            t={t}
            i18nKey='view.sla.banner.overdueSLADays'
            values={{
              days: -issue.slaDueDays,
            }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey='view.sla.banner.activeSLAMessage'
            values={getSLATimeValues()}
          />
        ))}
      <SLAConfirmModal
        isOpen={isSLAModalOpen}
        onAccept={handleAccept}
        onClose={handleClose}
        isLoading={updateSLAStatus.isPending}
        state={
          issue.slaEvent &&
          issue.slaEvent.eventType === IssueTimelineEventTypeEnum.SLADeactivated
            ? SLAStateEnum.ACTIVATED
            : SLAStateEnum.DEACTIVATED
        }
      />
    </>
  );
}
